import React from 'react';
import { Clock } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useElapsedTime } from '@/hooks/useElapsedTime';

/**
 * Props for ElapsedTimeDisplay component.
 */
export interface ElapsedTimeDisplayProps {
  /** ISO timestamp when the job started (null if not started) */
  startedAt: string | null;
  /** Elapsed seconds from server (synced on each poll) */
  serverElapsedSeconds: number | null;
  /** Estimated remaining seconds from server (optional) */
  estimatedRemainingSeconds?: number | null;
  /** Whether to show the estimated remaining time (default: true) */
  showEstimate?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Component that displays elapsed time and optional estimated remaining time.
 *
 * Features:
 * - Real-time updates (increments locally between polls)
 * - Server sync on each poll
 * - Human-readable time formatting
 * - Responsive sizing
 * - Dark mode support
 *
 * @example
 * ```tsx
 * <ElapsedTimeDisplay
 *   startedAt={jobStatus.started_at}
 *   serverElapsedSeconds={jobStatus.elapsed_seconds}
 *   estimatedRemainingSeconds={jobStatus.estimated_remaining_seconds}
 * />
 * ```
 */
export const ElapsedTimeDisplay: React.FC<ElapsedTimeDisplayProps> = ({
  startedAt,
  serverElapsedSeconds,
  estimatedRemainingSeconds = null,
  showEstimate = true,
  className = '',
  size = 'md',
}) => {
  const { t } = useTranslation();
  const { formattedElapsed, formattedRemaining, isRunning } = useElapsedTime({
    startedAt,
    serverElapsedSeconds,
    estimatedRemainingSeconds,
  });

  if (!isRunning) return null;

  const sizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
  };

  const iconSizes = {
    sm: 12,
    md: 16,
    lg: 20,
  };

  return (
    <div
      className={`flex items-center gap-2 text-secondary-500 dark:text-secondary-400 ${sizeClasses[size]} ${className}`}
    >
      <Clock size={iconSizes[size]} className="flex-shrink-0" />
      <span>
        {t('common.elapsedTime')}: <strong>{formattedElapsed}</strong>
      </span>
      {showEstimate && formattedRemaining && (
        <span className="text-secondary-400 dark:text-secondary-500">
          ({t('common.estimatedRemaining')}: ~{formattedRemaining})
        </span>
      )}
    </div>
  );
};
